const LOAD_EVENT_NAMES = ['DOMContentLoaded', 'turbolinks:load', 'turbo:load'];

const SELECTORS = {
  modal: ['#finished-modal', '#contract-finished-modal'],
  dateInput: '[data-finish-date-input], #site_actual_finished_at, #contract_actual_finished_at',
  commentInput: '[data-finish-excess-comment-input], #site_finish_excess_reason_comment, #contract_finish_excess_reason_comment',
  submitButton: '[data-finish-submit-button]'
};

const MODAL_SELECTOR = SELECTORS.modal.join(', ');
const EMPTY_EXCESS_STATE = {
  finishable: true,
  finish_unavailable_message: '',
  required: false,
  exceeded_elements: [],
  exceeded_contracts: []
};

let finishExcessRequestId = 0;

const nextFinishExcessRequestId = () => {
  finishExcessRequestId += 1;
  return finishExcessRequestId;
};

const setFinishExcessPending = (modal, pending) => {
  modal.dataset.finishExcessPending = pending ? 'true' : 'false';
  refreshFinishModalSubmitState(`#${modal.id}`);
};

export const isCommentFilled = (value) => String(value || '').trim().length > 0;

export const canSubmitFinish = ({ hasDate, commentRequired, commentValue }) => {
  return hasDate && (!commentRequired || isCommentFilled(commentValue || ''));
};

const parseBoolean = (value) => value === true || value === 'true';

const canSubmitModal = (modal) => {
  const dateInput = modal.querySelector(SELECTORS.dateInput);
  if (!dateInput) return false;

  // 判定応答待ち(pending)はここでは見ない。pending中のsubmitは handleFinishSubmit が予約して
  // 判定完了後に自動続行する(#3242: 1回目のクリックが無視され2回押す必要があった)
  if (parseBoolean(modal.dataset.finishUnavailable)) return false;

  const hasDate = dateInput.value.toString().trim().length > 0;
  const commentRequired = parseBoolean(modal.dataset.finishExcessCommentRequired);
  const commentInput = modal.querySelector(SELECTORS.commentInput);
  const commentValue = commentInput ? commentInput.value : '';

  return canSubmitFinish({ hasDate, commentRequired, commentValue });
};

const renderExceededNames = (modal, listSelector, exceededNames) => {
  const list = modal.querySelector(listSelector);
  if (!list) return;

  list.innerHTML = '';
  (exceededNames || []).forEach((name) => {
    const li = document.createElement('li');
    li.textContent = name;
    list.appendChild(li);
  });
};

const showModal = (modal) => {
  const modalApi = window.$ || window.jQuery;
  if (typeof modalApi === 'function') {
    modalApi(modal).modal('show');
  }
};

const disableSubmitButton = (modal) => {
  const submitButton = modal.querySelector(SELECTORS.submitButton);
  if (submitButton) {
    submitButton.disabled = true;
  }
};

const findModal = (target) => {
  if (!target || typeof target.closest !== 'function') return null;

  return target.closest(MODAL_SELECTOR) || (typeof target.querySelector === 'function' ? target.querySelector(MODAL_SELECTOR) : null);
};

const fetchExcessState = async ({ url, actualFinishedAt }) => {
  if (!url || !actualFinishedAt) {
    return EMPTY_EXCESS_STATE;
  }

  try {
    const response = await fetch(`${url}?actual_finished_at=${encodeURIComponent(actualFinishedAt)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) return null;

    return await response.json();
  } catch (_error) {
    return null;
  }
};

export const refreshFinishModalSubmitState = (modalSelector) => {
  const modal = document.querySelector(modalSelector);
  if (!modal) return;

  const submitButton = modal.querySelector(SELECTORS.submitButton);
  if (!submitButton) return;

  submitButton.disabled = !canSubmitModal(modal);
};

const renderFinishUnavailableState = (modal, message) => {
  modal.dataset.finishUnavailable = 'true';

  const unavailableMessage = modal.querySelector('[data-finish-unavailable-message]');
  if (unavailableMessage) {
    unavailableMessage.textContent = message || '';
    unavailableMessage.hidden = !message;
  }

  const container = modal.querySelector('[data-finish-excess-container]');
  if (container) {
    container.hidden = true;
  }

  const error = modal.querySelector('[data-finish-excess-error]');
  if (error) {
    error.textContent = '';
  }
};

const clearFinishUnavailableState = (modal) => {
  modal.dataset.finishUnavailable = 'false';
  const unavailableMessage = modal.querySelector('[data-finish-unavailable-message]');
  if (!unavailableMessage) return;

  unavailableMessage.textContent = '';
  unavailableMessage.hidden = true;
};

const renderFinishExcessState = ({
  modalSelector,
  listSelector,
  commentInputSelector,
  required,
  exceededNames,
  errorMessage,
  finishable,
  finishUnavailableMessage,
  show = true
}) => {
  const modal = document.querySelector(modalSelector);
  if (!modal) return;

  if (finishable === false) {
    renderFinishUnavailableState(modal, finishUnavailableMessage);
    refreshFinishModalSubmitState(modalSelector);
    if (show) showModal(modal);
    return;
  }

  clearFinishUnavailableState(modal);

  modal.dataset.finishExcessCommentRequired = required ? 'true' : 'false';

  const container = modal.querySelector('[data-finish-excess-container]');
  if (container) {
    container.hidden = !required;
  }

  const commentInput = modal.querySelector(commentInputSelector);
  if (commentInput && !required) {
    commentInput.value = '';
  }

  const error = modal.querySelector('[data-finish-excess-error]');
  if (error) {
    error.textContent = errorMessage || '';
  }

  renderExceededNames(modal, listSelector, exceededNames);
  refreshFinishModalSubmitState(modalSelector);
  if (show) showModal(modal);
};

export const renderSiteExcessState = (payload = {}) => {
  renderFinishExcessState({
    modalSelector: '#finished-modal',
    listSelector: '[data-finish-excess-contract-list]',
    commentInputSelector: '#site_finish_excess_reason_comment',
    required: payload.required,
    exceededNames: payload.exceededContracts,
    errorMessage: payload.errorMessage,
    finishable: payload.finishable,
    finishUnavailableMessage: payload.finishUnavailableMessage,
    show: payload.show
  });
};

export const renderContractExcessState = (payload = {}) => {
  renderFinishExcessState({
    modalSelector: '#contract-finished-modal',
    listSelector: '[data-finish-excess-element-list]',
    commentInputSelector: '#contract_finish_excess_reason_comment',
    required: payload.required,
    exceededNames: payload.exceededElements,
    errorMessage: payload.errorMessage,
    finishable: payload.finishable,
    finishUnavailableMessage: payload.finishUnavailableMessage,
    show: payload.show
  });
};

const renderFetchedExcessState = (modal, payload) => {
  if (!modal || !payload) return;

  if (modal.id === 'contract-finished-modal') {
    renderContractExcessState({
      finishable: payload.finishable,
      finishUnavailableMessage: payload.finish_unavailable_message || '',
      required: payload.required,
      exceededElements: payload.exceeded_elements || [],
      errorMessage: '',
      show: false
    });
    return;
  }

  if (modal.id === 'finished-modal') {
    renderSiteExcessState({
      finishable: payload.finishable,
      finishUnavailableMessage: payload.finish_unavailable_message || '',
      required: payload.required,
      exceededContracts: payload.exceeded_contracts || [],
      errorMessage: '',
      show: false
    });
  }
};

const refreshAllModals = () => {
  SELECTORS.modal.forEach((selector) => refreshFinishModalSubmitState(selector));
};

const parseAjaxErrorPayload = (event) => {
  const [data, _status, xhr] = event.detail || [];
  if (data && typeof data === 'object') return data;
  if (xhr && xhr.responseJSON && typeof xhr.responseJSON === 'object') return xhr.responseJSON;

  const responseText = typeof data === 'string' ? data : xhr && xhr.responseText;
  if (!responseText) return null;

  try {
    return JSON.parse(responseText);
  } catch (_error) {
    return null;
  }
};

const handleAjaxError = (event) => {
  const form = event.target;
  if (!form || typeof form.matches !== 'function') return;

  const xhr = event.detail && event.detail[2];
  if (!xhr || xhr.status !== 422) return;

  const payload = parseAjaxErrorPayload(event);
  if (!payload) return;

  if (form.matches('[data-finish-contract-form]')) {
    renderContractExcessState({
      finishable: payload.finishable,
      finishUnavailableMessage: payload.finish_unavailable_message || '',
      required: payload.required,
      exceededElements: payload.exceeded_elements || [],
      errorMessage: payload.error_message
    });
    return;
  }

  if (!form.matches('[data-finish-site-form]')) return;

  renderSiteExcessState({
    finishable: payload.finishable,
    finishUnavailableMessage: payload.finish_unavailable_message || '',
    required: payload.required,
    exceededContracts: payload.exceeded_contracts || [],
    errorMessage: payload.error_message
  });
};

const finishForm = (modal) =>
  modal.querySelector('[data-finish-site-form], [data-finish-contract-form]') ||
  (typeof modal.closest === 'function' ? modal.closest('[data-finish-site-form], [data-finish-contract-form]') : null);

// 判定応答待ち中に予約されたsubmitを、判定完了後の最終状態で送信可能なら自動続行する。
// コメント必須・終了不可・判定失敗になった場合は送信せず、従来どおり画面表示に任せる。
const resumeQueuedSubmit = (modal) => {
  if (modal.dataset.finishSubmitQueued !== 'true') return;

  modal.dataset.finishSubmitQueued = 'false';
  if (!canSubmitModal(modal)) return;

  const form = finishForm(modal);
  if (!form) return;

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
  } else {
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }
};

const handleFinishDateChange = async (target) => {
  const modal = findModal(target);
  if (!modal) return;

  // 日付が入力し直されたら、以前の送信予約は破棄する
  modal.dataset.finishSubmitQueued = 'false';

  const requestedFinishedAt = target.value;
  const requestId = nextFinishExcessRequestId();
  modal.dataset.finishExcessRequestId = String(requestId);

  if (!requestedFinishedAt) {
    setFinishExcessPending(modal, false);
    renderFetchedExcessState(modal, EMPTY_EXCESS_STATE);
    return;
  }

  setFinishExcessPending(modal, true);

  const payload = await fetchExcessState({
    url: modal.dataset.finishExcessResultUrl,
    actualFinishedAt: requestedFinishedAt
  });

  if (modal.dataset.finishExcessRequestId !== String(requestId) || target.value !== requestedFinishedAt) {
    return;
  }

  if (!payload) {
    setFinishExcessPending(modal, false);
    renderFetchedExcessState(modal, {
      finishable: false,
      finish_unavailable_message: '終了可否の判定に失敗しました。再度終了日を入力してください。',
      required: false,
      exceeded_elements: [],
      exceeded_contracts: []
    });
    resumeQueuedSubmit(modal);
    return;
  }

  setFinishExcessPending(modal, false);
  renderFetchedExcessState(modal, payload);
  resumeQueuedSubmit(modal);
};

const handleFinishInputChange = (event) => {
  const target = event.target;
  if (!target || typeof target.matches !== 'function') return;

  if (target.matches(SELECTORS.commentInput)) {
    refreshAllModals();
    return;
  }

  if (target.matches(SELECTORS.dateInput)) {
    handleFinishDateChange(target);
  }
};

const bindInputEvents = () => {
  document.addEventListener('change', handleFinishInputChange);
  document.addEventListener('input', handleFinishInputChange);
};

const bindAjaxErrorEvents = () => {
  document.addEventListener('ajax:error', handleAjaxError);
};

const bindSubmitEvents = () => {
  document.addEventListener('submit', handleFinishSubmit, true);
};

const bindEvents = () => {
  if (document.body.dataset.finishExcessReasonModalBound === 'true') return;
  document.body.dataset.finishExcessReasonModalBound = 'true';

  bindInputEvents();
  bindAjaxErrorEvents();
  bindSubmitEvents();

  refreshAllModals();
};

LOAD_EVENT_NAMES.forEach((eventName) => {
  document.addEventListener(eventName, bindEvents);
});

window.FinishExcessReasonModal = {
  refresh: refreshFinishModalSubmitState,
  renderSiteExcessState,
  renderContractExcessState
};

const blockSubmitEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
  if (typeof event.stopImmediatePropagation === 'function') {
    event.stopImmediatePropagation();
  }
};

const handleFinishSubmit = (event) => {
  const form = event.target;
  if (!form || typeof form.matches !== 'function') return;
  if (!form.matches('[data-finish-site-form], [data-finish-contract-form]')) return;

  const modal = findModal(form);
  if (!modal) return;

  refreshFinishModalSubmitState(`#${modal.id}`);

  // 判定応答待ち中のsubmitは送信を予約して止める。判定完了後に resumeQueuedSubmit が自動続行する(#3242)
  if (parseBoolean(modal.dataset.finishExcessPending)) {
    modal.dataset.finishSubmitQueued = 'true';
    disableSubmitButton(modal);
    blockSubmitEvent(event);
    return;
  }

  if (canSubmitModal(modal)) {
    disableSubmitButton(modal);
    return;
  }

  blockSubmitEvent(event);
};
